import { defineStore } from 'pinia';
import { authService } from '@/services/authService';
import type { User, LoginRequest, RegisterRequest } from '@/types/auth';
import router from '@/router';

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null as User | null,
    token: localStorage.getItem('token') || null,
    loading: false,
    error: null as string | null,
  }),

  getters: {
    isAuthenticated: (state) => !!state.token,
    currentUser: (state) => state.user,
  },

  actions: {
    setToken(token: string) {
      this.token = token;
      localStorage.setItem('token', token);
    },

    clearToken() {
      this.token = null;
      localStorage.removeItem('token');
    },

    setUser(user: User) {
      this.user = user;
    },

    async sendRegisterCode(email: string) {
      try {
        this.loading = true;
        this.error = null;
        await authService.sendRegisterCode(email);
        return true;
      } catch (error: any) {
        this.error = error.response?.data?.message || '发送验证码失败';
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async verifyRegisterCode(email: string, code: string) {
      try {
        this.loading = true;
        this.error = null;
        await authService.verifyRegisterCode(email, code);
        return true;
      } catch (error: any) {
        this.error = error.response?.data?.message || '验证码验证失败';
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async register(registerData: RegisterRequest) {
      try {
        this.loading = true;
        this.error = null;
        const response = await authService.register(registerData);
        this.setToken(response.data!.token);
        this.setUser(response.data!.user);
        router.push('/');
        return true;
      } catch (error: any) {
        this.error = error.response?.data?.message || '注册失败';
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async login(loginData: LoginRequest) {
      try {
        this.loading = true;
        this.error = null;
        const response = await authService.login(loginData);
        this.setToken(response.data!.token);
        this.setUser(response.data!.user);
        router.push('/');
        return true;
      } catch (error: any) {
        this.error = error.response?.data?.message || '登录失败';
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async forgotPassword(email: string) {
      try {
        this.loading = true;
        this.error = null;
        await authService.forgotPassword(email);
        return true;
      } catch (error: any) {
        this.error = error.response?.data?.message || '发送重置邮件失败';
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async resetPassword(token: string, newPassword: string) {
      try {
        this.loading = true;
        this.error = null;
        await authService.resetPassword(token, newPassword);
        return true;
      } catch (error: any) {
        this.error = error.response?.data?.message || '重置密码失败';
        throw error;
      } finally {
        this.loading = false;
      }
    },

    logout() {
      this.user = null;
      this.clearToken();
      router.push('/login');
    },
  },
}); 